import { computed } from 'vue'
import { useCookie } from 'nuxt/app'
import { NewFeatureSection } from '~/actions/newFeature/action'
import { TripNavigation } from '~/services/navigation/trip'

export type MenuView =
	| 'dashboard'
	| 'spots'
	| 'itinerary'
	| 'tasks'
	| 'documents'
	| 'store/ideas'
	| 'store/activities'
	| 'store/indispensables'
	| 'wall'

export type MenuItem = {
	name: string
	icon: string
	action: () => void
	view?: MenuView
	hide?: boolean
	isNew?: boolean
}

export const SECTION_NEW_FEATURE: Partial<Record<MenuView, NewFeatureSection>> = {
	'store/activities': NewFeatureSection.ACTIVITIES,
	'store/ideas': NewFeatureSection.IDEAS,
	'store/indispensables': NewFeatureSection.INDISPENSABLES,
}

export function useTripMenu(tripId: string) {
	const isNew = (view: MenuView): boolean => {
		const cookieKey = SECTION_NEW_FEATURE[view]
		if (!cookieKey) return false
		return !useCookie(cookieKey).value
	}

	const { hasProducts, hasActivities } = useTrip(tripId)

	const menuItems = computed<MenuItem[]>(() => [
		{
			name: 'web.trip.sidebar.summary',
			icon: 'grid-four',
			action: () => TripNavigation.toTripDashboard(tripId),
			view: 'dashboard',
		},
		{
			name: 'web.trip.sidebar.places',
			icon: 'bookmark-simple',
			action: () => TripNavigation.toTripSpots(tripId),
			view: 'spots',
		},
		{
			name: 'web.trip.sidebar.itinerary',
			icon: 'map-trifold',
			action: () => TripNavigation.toTripItinerary(tripId),
			view: 'itinerary',
		},
		{
			name: 'web.trip.sidebar.tasks',
			icon: 'clipboard-text',
			action: () => TripNavigation.toTripTasks(tripId),
			view: 'tasks',
		},
		{
			name: 'web.board.header.title',
			icon: 'note-blank',
			action: () => TripNavigation.toTripWall(tripId),
			view: 'wall',
			isNew: isNew('wall'),
		},
		{
			name: 'web.trip.sidebar.bookings',
			icon: 'file-text',
			action: () => TripNavigation.toTripDocuments(tripId),
			view: 'documents',
		},
	])

	const storeMenuItems = computed<MenuItem[]>(() => [
		{
			name: 'web.trip.sidebar.ideas',
			icon: 'suitcase',
			action: () => TripNavigation.toTripProductStore(tripId),
			view: 'store/ideas',
			isNew: isNew('store/ideas'),
			hide: !hasProducts.value,
		},
		{
			name: 'web.trip.sidebar.activities',
			icon: 'ticket',
			action: () => TripNavigation.toTripActivitiesStore(tripId),
			view: 'store/activities',
			isNew: isNew('store/activities'),
			hide: !hasActivities.value,
		},
		{
			name: 'web.trip.sidebar.essential',
			icon: 'globe',
			action: () => TripNavigation.toTripIndispensablesStore(tripId),
			view: 'store/indispensables',
			isNew: isNew('store/indispensables'),
		},
	])

	return { menuItems, storeMenuItems }
}
